import LegalPageLayout, { type LegalSection } from '../components/layout/LegalPageLayout';
import Seo from '../components/Seo';
import '../styles/legal-pages.css';

const sections: LegalSection[] = [
  { id: 'general', title: '1. Общие положения' },
  { id: 'definitions', title: '2. Термины и определения' },
  { id: 'subject', title: '3. Предмет и условия оказания услуг' },
  { id: 'account', title: '4. Регистрация и учетная запись' },
  { id: 'rights', title: '5. Права и обязанности сторон' },
  { id: 'payments', title: '6. Тарифы, порядок оплаты' },
  { id: 'ip', title: '7. Интеллектуальная собственность' },
  { id: 'liability', title: '8. Ответственность и ограничения' },
  { id: 'pd', title: '9. Персональные данные' },
  { id: 'termination', title: '10. Срок действия и расторжение' },
  { id: 'force', title: '11. Форс‑мажор' },
  { id: 'disputes', title: '12. Разрешение споров' },
  { id: 'final', title: '13. Заключительные положения и реквизиты' },
];

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Пользовательское соглашение"
      updatedAt="28 января 2026 г."
      version="1.0"
      sections={sections}
    >
      <Seo
        title="Пользовательское соглашение — Recvisit"
        description="Условия использования SaaS‑сервиса Recvisit, права и обязанности сторон, ответственность и порядок разрешения споров."
        canonicalPath="/terms"
      />
      <section id="general" className="legal-section">
        <h2>1. Общие положения</h2>
        <p>
          Настоящее Пользовательское соглашение (далее — <strong>Соглашение</strong>) определяет условия использования
          программного обеспечения и веб‑сервиса <strong>Recvisit</strong> (далее — <strong>Сервис</strong>) и порядок
          предоставления доступа к нему.
        </p>
        <p>
          Исполнитель: <strong>Индивидуальный предприниматель Шмелева Екатерина Владимировна</strong> (ИНН 482306497385,
          ОГРНИП 324480000041882, адрес: Липецкая область, г. Липецк, ул. Советская, д. 4, оф. 619) (далее —
          <strong> Исполнитель</strong>).
        </p>
        <p>
          <strong>Важно:</strong> Услуги предоставляются исключительно юридическим лицам и индивидуальным предпринимателям, 
          зарегистрированным в соответствии с законодательством Российской Федерации. Физические лица, не являющиеся 
          индивидуальными предпринимателями, не могут быть Пользователями и не имеют права использовать Сервис.
        </p>
        <p>
          Пользователь: юридическое лицо или индивидуальный предприниматель (далее — <strong>Клиент</strong>), использующий 
          Сервис, а также уполномоченный представитель Клиента (физическое лицо), действующий от имени и в интересах Клиента 
          при использовании Сервиса (далее — <strong>Пользователь</strong>). Клиент является работодателем, в интересах 
          которого осуществляется учет посещаемости.
        </p>
        <p>
          Начало использования Сервиса (включая регистрацию, авторизацию, использование функционала Сервиса) означает
          полное и безоговорочное принятие условий настоящего Соглашения.
        </p>
      </section>

      <section id="definitions" className="legal-section">
        <h2>2. Термины и определения</h2>
        <ul>
          <li>
            <strong>Сервис (Recvisit)</strong> — программное обеспечение и связанные компоненты (веб‑интерфейс,
            мобильные/планшетные приложения и API при наличии), предоставляемые по модели SaaS.
          </li>
          <li>
            <strong>Клиент</strong> — юридическое лицо или индивидуальный предприниматель (работодатель), в интересах 
            которого фиксируются события посещаемости и который является стороной договора с Исполнителем.
          </li>
          <li>
            <strong>Пользователь</strong> — Клиент или уполномоченный представитель Клиента (физическое лицо), использующий 
            Сервис от имени и в интересах Клиента.
          </li>
          <li>
            <strong>Учетная запись</strong> — совокупность данных, позволяющих Пользователю получить доступ к Сервису.
          </li>
          <li>
            <strong>Сотрудник</strong> — лицо, посещения которого фиксируются в Сервисе по поручению Клиента.
          </li>
          <li>
            <strong>Контент Пользователя</strong> — данные, загружаемые/вносимые Пользователем в Сервис (в т.ч. данные
            сотрудников, объекты, события посещаемости, изображения и т.п.).
          </li>
        </ul>
      </section>

      <section id="subject" className="legal-section">
        <h2>3. Предмет и условия оказания услуг</h2>
        <p>
          Исполнитель предоставляет Пользователю доступ к Сервису на условиях настоящего Соглашения и выбранного
          тарифного плана. Сервис предоставляется по модели SaaS: Сервис развернут и функционирует на инфраструктуре
          Исполнителя, а Пользователь получает доступ удаленно через сеть Интернет.
        </p>
        <p>
          Функциональность Сервиса может изменяться, дополняться и обновляться Исполнителем. Информация о текущих
          возможностях, ограничениях, стоимости и правилах использования размещается на сайте и/или в интерфейсе
          Сервиса.
        </p>
      </section>

      <section id="account" className="legal-section">
        <h2>4. Регистрация и учетная запись</h2>
        <ul>
          <li>Для доступа к Сервису требуется регистрация и/или авторизация.</li>
          <li>Пользователь обязан предоставлять достоверные данные и поддерживать их актуальность.</li>
          <li>
            Пользователь несет ответственность за сохранность учетных данных и за все действия, совершенные с
            использованием его учетной записи.
          </li>
          <li>
            Исполнитель вправе приостановить доступ при наличии признаков компрометации учетной записи, угрозы
            безопасности, нарушений условий Соглашения либо при наличии требований уполномоченных органов.
          </li>
        </ul>
      </section>

      <section id="rights" className="legal-section">
        <h2>5. Права и обязанности сторон</h2>
        <h3>5.1. Исполнитель обязуется</h3>
        <ul>
          <li>Предоставлять доступ к Сервису в соответствии с условиями Соглашения и тарифного плана.</li>
          <li>
            Предпринимать разумные меры для обеспечения доступности Сервиса, за исключением периодов технических работ и
            обстоятельств, не зависящих от Исполнителя.
          </li>
          <li>Обеспечивать безопасность обработки данных в пределах разумных технических и организационных мер.</li>
        </ul>

        <h3>5.2. Исполнитель вправе</h3>
        <ul>
          <li>Обновлять и изменять функциональность, интерфейс и документацию Сервиса.</li>
          <li>Временно ограничивать доступ для технических работ, обновлений и устранения инцидентов.</li>
          <li>
            Приостанавливать или прекращать доступ при нарушении Соглашения, при угрозе безопасности или по требованиям
            государственных органов.
          </li>
        </ul>

        <h3>5.3. Пользователь обязуется</h3>
        <ul>
          <li>Использовать Сервис в соответствии с законодательством РФ и условиями Соглашения.</li>
          <li>
            Не осуществлять действий, направленных на нарушение работоспособности Сервиса, не пытаться получить
            несанкционированный доступ, не обходить ограничения и меры защиты.
          </li>
          <li>
            Обеспечить законность обработки персональных данных сотрудников: наличие правовых оснований и необходимых
            согласий субъектов персональных данных, если такие согласия требуются.
          </li>
          <li>
            Гарантировать наличие всех необходимых правовых оснований и согласий субъектов персональных данных 
            (сотрудников) на обработку их персональных данных в Сервисе, включая передачу данных Исполнителю для 
            обработки по поручению. Клиент несет полную ответственность за отсутствие таких согласий и правовых оснований, 
            а также обязуется возместить Исполнителю все убытки, возникшие в связи с нарушением данного обязательства.
          </li>
        </ul>

        <h3>5.4. Запрещенные действия</h3>
        <ul>
          <li>Использование Сервиса в незаконных, мошеннических или иных противоправных целях.</li>
          <li>
            Попытки обхода технических ограничений, взлома, DDoS-атак и иных действий, направленных на нарушение 
            работоспособности Сервиса.
          </li>
          <li>Использование данных, полученных из Сервиса, для создания конкурентных продуктов или услуг.</li>
          <li>Извлечение данных в коммерческих целях без согласия Исполнителя.</li>
          <li>Злоупотребление правом на возврат средств (например, многократные регистрации с целью возврата).</li>
        </ul>
        <p>
          При нарушении данных запретов Исполнитель вправе немедленно прекратить доступ без возврата средств и потребовать 
          возмещения ущерба.
        </p>
      </section>

      <section id="payments" className="legal-section">
        <h2>6. Тарифы, порядок оплаты</h2>
        <p>
          Условия тарифных планов, стоимость и порядок оплаты размещаются на сайте и/или в интерфейсе Сервиса. Оплата
          производится на условиях предоплаты, если иное явно не предусмотрено отдельным соглашением сторон.
        </p>
        <p>
          Исполнитель вправе изменять стоимость тарифов, уведомляя Пользователя заблаговременно (в том числе путем
          публикации на сайте/в интерфейсе). Изменение стоимости не распространяется на уже оплаченные периоды.
        </p>
      </section>

      <section id="ip" className="legal-section">
        <h2>7. Интеллектуальная собственность</h2>
        <p>
          Исключительные права на Сервис, его программный код, дизайн, базы данных и иные результаты интеллектуальной
          деятельности принадлежат Исполнителю либо используются на законных основаниях.
        </p>
        <p>
          Пользователю предоставляется неисключительное, непередаваемое право использования Сервиса на срок действия
          Соглашения исключительно по его функциональному назначению.
        </p>
        <p>
          Запрещены: копирование, модификация, декомпиляция, реверс‑инжиниринг, обход технических ограничений, а также
          иное использование Сервиса вне предусмотренных Соглашением целей.
        </p>
        <p>
          Запрещается использование данных, полученных из Сервиса, для создания конкурентных продуктов или услуг, а также 
          извлечение данных в коммерческих целях без согласия Исполнителя. Любые производные продукты, созданные на основе 
          данных или функциональности Сервиса, являются нарушением исключительных прав Исполнителя.
        </p>
      </section>

      <section id="liability" className="legal-section">
        <h2>8. Ответственность и ограничения</h2>
        <p>
          Сервис предоставляется по принципу «как есть» (<em>as is</em>) в пределах, допускаемых применимым
          законодательством. Исполнитель не гарантирует абсолютную бесперебойность и безошибочность работы Сервиса.
        </p>
        <p>
          Ответственность Исполнителя ограничена суммой оплаты за последний оплаченный период использования Сервиса и не
          включает упущенную выгоду, косвенные и специальные убытки.
        </p>
        <p>
          Исполнитель не несет ответственности за косвенные убытки, упущенную выгоду, потерю данных, коммерческие потери 
          и иные убытки, не связанные напрямую с нарушением обязательств Исполнителя.
        </p>
        <p>
          Исполнитель не несет ответственности за действия Пользователя и третьих лиц, за качество и работу устройств и
          каналов связи Пользователя, а также за законность обработки персональных данных сотрудников Клиента. Клиент 
          несет полную ответственность перед третьими лицами (включая сотрудников) за обработку их персональных данных и 
          обязуется самостоятельно урегулировать все претензии третьих лиц, связанные с обработкой персональных данных 
          в Сервисе.
        </p>
        <p>
          Исполнитель не несет ответственности перед третьими лицами за использование Сервиса Клиентом и обязуется 
          не предъявлять к Исполнителю претензий от имени третьих лиц.
        </p>
      </section>

      <section id="pd" className="legal-section">
        <h2>9. Персональные данные</h2>
        <p>
          Порядок обработки персональных данных определяется Политикой конфиденциальности. Для данных сотрудников Клиента
          Клиент выступает оператором персональных данных, а Исполнитель — лицом, обрабатывающим персональные данные по
          поручению Клиента (в части, применимой к модели использования).
        </p>
        <p>
          Условия обработки персональных данных по поручению определяются настоящим Соглашением и Политикой конфиденциальности. 
          Акцепт настоящего Соглашения означает заключение договора обработки персональных данных по поручению между Клиентом 
          (оператором) и Исполнителем (обработчиком) на условиях, изложенных в указанных документах.
        </p>
      </section>

      <section id="termination" className="legal-section">
        <h2>10. Срок действия и расторжение</h2>
        <p>Соглашение действует с момента принятия Пользователем условий и до его расторжения одной из сторон.</p>
        <p>
          Пользователь вправе прекратить использование Сервиса. Исполнитель вправе прекратить/ограничить доступ при
          нарушении Соглашения, угрозе безопасности или по иным основаниям, предусмотренным Соглашением и законом.
        </p>
      </section>

      <section id="force" className="legal-section">
        <h2>11. Форс‑мажор</h2>
        <p>
          Стороны освобождаются от ответственности за частичное или полное неисполнение обязательств по Соглашению, если
          такое неисполнение явилось следствием обстоятельств непреодолимой силы (форс‑мажор), включая, помимо прочего:
          стихийные бедствия, военные действия, решения органов власти, массовые кибератаки, сбои магистральных
          провайдеров связи и т.п.
        </p>
      </section>

      <section id="disputes" className="legal-section">
        <h2>12. Разрешение споров</h2>
        <p>
          Претензионный порядок обязателен. Срок рассмотрения претензии — до 30 календарных дней с даты получения.
        </p>
        <p>
          При недостижении согласия спор подлежит рассмотрению в суде по месту жительства Исполнителя (г. Липецк,
          Липецкая область), если иное не предусмотрено императивными нормами законодательства.
        </p>
      </section>

      <section id="final" className="legal-section">
        <h2>13. Заключительные положения и реквизиты</h2>
        <p>
          Исполнитель вправе изменять условия Соглашения путем публикации новой редакции на сайте. Продолжение
          использования Сервиса после публикации изменений означает согласие Пользователя с новой редакцией.
        </p>
        <h3>Реквизиты Исполнителя</h3>
        <div className="legal-box">
          <p><strong>Индивидуальный предприниматель Шмелева Екатерина Владимировна</strong></p>
          <p>ИНН: 482306497385</p>
          <p>ОГРНИП: 324480000041882</p>
          <p>Адрес: Липецкая область, г. Липецк, ул. Советская, д. 4, оф. 619</p>
          <p>Email: <a href="mailto:info@recvi.ru">info@recvi.ru</a></p>
          <p>Сайт: <a href="https://recvi.ru" target="_blank" rel="noopener noreferrer">recvi.ru</a></p>
        </div>
      </section>
    </LegalPageLayout>
  );
}

